import { searchResultsData } from 'common/components/entities/search-results/utils'
import {
  SearchResponseInterface,
  SearchResultInterface,
} from 'common/types/search-interfaces'
import useBlog, { selectors as blogSelectors } from 'publisher/hooks/useBlog'
import {
  managementSelectors,
  typedPageSelectors,
  useManagement,
  usePage,
} from 'publisher/store'
import { useEffect, useState } from 'react'
import { getBlogSearch } from 'src/publisher/api/blog-search-api'
import { getWebsiteSearch } from 'src/publisher/api/website-search-api'
import { SearchSourceType } from 'src/publisher/entities/search/types'
import { resolveSearchBasePath } from 'src/publisher/entities/search/utils/resolve-search-base-path'

interface UseSearchResultsProps {
  source?: SearchSourceType
  limit?: number
}

interface SearchResultsResponseInterface
  extends SearchResponseInterface<SearchResultInterface> {
  pages: number
}

interface SearchParamsInterface {
  query: null | string
  page: null | string
  limit: null | string
}

export const useSearchResults = ({
  source = 'blog',
  limit = 10,
}: UseSearchResultsProps) => {
  const isPreview = useManagement(managementSelectors.isPreview)
  const locale = usePage(state => typedPageSelectors.getLocale(state))
  const blogPath = resolveSearchBasePath(useBlog(blogSelectors.getBlogPath))
  const [error, setError] = useState<unknown>(null)
  const [isFetching, setIsFetching] = useState(false)

  const getPreviewSearchResults = (): SearchResultsResponseInterface => ({
    page: 1,
    pages: 3,
    total: 45,
    data: searchResultsData(limit),
  })

  const [searchResults, setSearchResults] =
    useState<SearchResultsResponseInterface | null>(
      isPreview ? getPreviewSearchResults() : null,
    )

  const fetch = async (params: SearchParamsInterface) => {
    setIsFetching(true)
    try {
      const requestedLimit = params.limit ? params.limit : limit.toString()
      const data =
        source === 'website'
          ? await getWebsiteSearch({
              query: params.query,
              limit: requestedLimit,
              page: params.page,
              locale,
            })
          : await getBlogSearch({
              blogPath,
              query: params.query,
              limit: requestedLimit,
              page: params.page,
            })

      setSearchResults({
        ...data,
        pages: Math.ceil(data.total / (params.limit ? +params.limit : limit)),
      })
    } catch (nextError) {
      setError(nextError)
    } finally {
      setIsFetching(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once on mount
  useEffect(() => {
    const params = new URLSearchParams(document.location.search)
    fetch({
      query: params.get('query'),
      page: params.get('page'),
      limit: params.get('limit'),
    })
  }, [])

  return { searchResults, isFetching, error, fetch, isPreview }
}
